// src/Pages/PurchaseConfirmation.js - Flujo de 3 Pasos

import React, { useState, useEffect, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import publicApi from '../api/publicApi';
import api from '../api/api';
import styles from './PurchaseConfirmation.module.css';
import { useToast } from '../Components/ui/Toast.jsx';
import { useConfirm } from '../Components/ui/ConfirmDialog.jsx';

// --- FUNCIÓN QUE DEFINE EL LAYOUT DETALLADO DEL BUS ---
const getDetailedBusLayout = (busSeatCount) => {
    const layout = {
        lower: [],
        upper: [],
        totalSeats: 0
    };

    if (!busSeatCount) {
        return layout;
    }

    if (busSeatCount === 52) { // Yutong (Piso Único)
        layout.totalSeats = 52;
        let seatNum = 1;

        // Fila 1 (Delantera)
        layout.lower.push([
            { id: 'driver', type: 'special' },
            { id: 'empty', type: 'empty' },
            { id: 'empty', type: 'empty' },
            'aisle',
            { id: 'empty', type: 'empty' },
            { id: 'empty', type: 'empty' }
        ]);

        // Filas estándar de 4 asientos
        while (seatNum <= layout.totalSeats - 4) {
            layout.lower.push([
                { id: seatNum++, type: 'seat' },
                { id: seatNum++, type: 'seat' },
                'aisle',
                { id: seatNum++, type: 'seat' },
                { id: seatNum++, type: 'seat' },
                { id: 'empty', type: 'empty' }
            ]);
        }

        // Última fila con asientos 49–52 (2 asientos - pasillo - 2 asientos) + columna vacía para alineación
        layout.lower.push([
            { id: seatNum++, type: 'seat' },
            { id: seatNum++, type: 'seat' },
            'aisle',
            { id: seatNum++, type: 'seat' },
            { id: seatNum++, type: 'seat' },
            { id: 'empty', type: 'empty' }
        ]);

        // Fila exclusiva para el baño
        layout.lower.push([
            { id: 'toilet', type: 'special' },
            { id: 'empty', type: 'empty' },
            { id: 'empty', type: 'empty' },
            'aisle',
            { id: 'empty', type: 'empty' },
            { id: 'empty', type: 'empty' }
        ]);

    } else if (busSeatCount === 56) { // Doble Piso (40 arriba, 16 abajo)
        layout.totalSeats = 56;

        // Fila 1: Chofer
        layout.lower.push([
            { id: 'driver', type: 'special' },
            { id: 'empty', type: 'empty' },
            { id: 'empty', type: 'empty' },
            'aisle',
            { id: 'empty', type: 'empty' },
            { id: 'empty', type: 'empty' }
        ]);

        // Fila 2: baño + escaleras
        layout.lower.push([
            { id: 'toilet', type: 'special' },
            { id: 'stairs2', type: 'special' },
            { id: 'empty', type: 'empty' },
            'aisle',
            { id: 'empty', type: 'empty' },
            { id: 'empty', type: 'empty' }
        ]);

        // Asientos del piso inferior (41 al 56)
        let seatNumLower = 41;
        while (seatNumLower <= 56) {
            layout.lower.push([
                { id: seatNumLower++, type: 'seat' },
                { id: seatNumLower++, type: 'seat' },
                'aisle',
                { id: seatNumLower++, type: 'seat' },
                { id: seatNumLower++, type: 'seat' }
            ]);
        }

        // Piso superior (1 al 40)
        let seatNumUpper = 1;
        while (seatNumUpper <= 40) {
            layout.upper.push([
                { id: seatNumUpper++, type: 'seat' },
                { id: seatNumUpper++, type: 'seat' },
                'aisle',
                { id: seatNumUpper++, type: 'seat' },
                { id: seatNumUpper++, type: 'seat' }
            ]);
        }

    } else if (busSeatCount === 60) { // Doble Piso (44 arriba, 16 abajo)
        layout.totalSeats = 60;

        // Fila 1: Chofer
        layout.lower.push([
            { id: 'driver', type: 'special' },
            { id: 'empty', type: 'empty' },
            { id: 'empty', type: 'empty' },
            'aisle',
            { id: 'empty', type: 'empty' },
            { id: 'empty', type: 'empty' }
        ]);

        // Fila 2: baño + escaleras
        layout.lower.push([
            { id: 'toilet', type: 'special' },
            { id: 'stairs2', type: 'special' },
            { id: 'empty', type: 'empty' },
            'aisle',
            { id: 'empty', type: 'empty' },
            { id: 'empty', type: 'empty' }
        ]);

        // Asientos del piso inferior (45 al 60)
        let seatNumLower = 45;
        while (seatNumLower <= 60) {
            layout.lower.push([
                { id: seatNumLower++, type: 'seat' },
                { id: seatNumLower++, type: 'seat' },
                'aisle',
                { id: seatNumLower++, type: 'seat' },
                { id: seatNumLower++, type: 'seat' }
            ]);
        }

        // Piso superior (1 al 44)
        let seatNumUpper = 1;
        while (seatNumUpper <= 44) {
            layout.upper.push([
                { id: seatNumUpper++, type: 'seat' },
                { id: seatNumUpper++, type: 'seat' },
                'aisle',
                { id: seatNumUpper++, type: 'seat' },
                { id: seatNumUpper++, type: 'seat' }
            ]);
        }
    }

    return layout;
};


// Generar un ID de sesión único para el bloqueo de asientos
const generateSessionId = () => {
  const stored = sessionStorage.getItem('seatLockSessionId');
  if (stored) return stored;
  const newId = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  sessionStorage.setItem('seatLockSessionId', newId);
  return newId;
};

const PurchaseConfirmation = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { showToast } = useToast();
  const { confirm } = useConfirm();

  // Estados
  const [currentStep, setCurrentStep] = useState(1); // 1: Asientos, 2: Pasajeros, 3: Pago
  const [loading, setLoading] = useState(false);
  const [selectedSeats, setSelectedSeats] = useState([]);
  const [occupiedSeats, setOccupiedSeats] = useState([]);
  const [lockedSeats, setLockedSeats] = useState([]); // Asientos bloqueados por otros usuarios
  const [userDataPerSeat, setUserDataPerSeat] = useState({});
  const [sessionSeatId, setSessionSeatId] = useState(null);
  const [clientId, setClientId] = useState(null);
  const [sessionLoading, setSessionLoading] = useState(true);
  const [seatMappings, setSeatMappings] = useState({}); // Map seat number -> Seat ID
  const [lockSessionId] = useState(generateSessionId); // ID único de sesión para bloqueos
  const [lockingInProgress, setLockingInProgress] = useState(false); // Evitar doble clic

  // Estados para promociones
  const [availablePromotions, setAvailablePromotions] = useState([]); // Promociones automáticas
  const [appliedPromotion, setAppliedPromotion] = useState(null); // Promoción aplicada
  const [promoCode, setPromoCode] = useState(''); // Código promocional ingresado
  const [promoCodeError, setPromoCodeError] = useState(''); // Error de código
  const [promoCodeLoading, setPromoCodeLoading] = useState(false);

  const travelDetails = location.state?.travelDetails || JSON.parse(localStorage.getItem("lastTravelDetails"));
  const tripId = travelDetails?.ID || travelDetails?.id;
  const busSeatCount = travelDetails?.Bus_Seats || travelDetails?.bus_capacity || 0;

  // useEffect inicial
  // Cargar clientId (se ejecuta al montar y cuando cambia localStorage)
  useEffect(() => {
    const loadClientId = () => {
      const storedUser = localStorage.getItem('currentUser');
      let parsedUser = null;
      try {
        if(storedUser) {
          parsedUser = JSON.parse(storedUser);
        }
      } catch (error) {

      }

      if (parsedUser) {
        setSessionLoading(true);
        api.get('/my_client')
          .then(res => {
            setClientId(res.data.ID);
          })
          .catch(() => {
            setClientId(null);
          })
          .finally(() => {
            setSessionLoading(false);
          });
      } else {
        setClientId(null);
        setSessionLoading(false);
      }
    };

    loadClientId();

    // Escuchar cambios en localStorage (cuando el usuario hace login desde otra pestaña)
    const handleStorageChange = (e) => {
      if (e.key === 'currentUser') {

        loadClientId();
      }
    };

    window.addEventListener('storage', handleStorageChange);

    // También escuchar el evento personalizado para cambios en la misma pestaña
    const handleSessionChange = () => {

      loadClientId();
    };
    window.addEventListener('sessionChanged', handleSessionChange);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('sessionChanged', handleSessionChange);
    };
  }, []);

  // Función para cargar asientos ocupados y bloqueados
  const fetchOccupiedSeats = async () => {
    if (!tripId) return;
    try {
      const res = await publicApi.get(`/occupied_seats/${tripId}?session_id=${lockSessionId}`);
      const booked = res.data.bookedSeats || [];
      const locked = res.data.lockedSeats || [];
      setOccupiedSeats(booked);
      setLockedSeats(locked);

    } catch (err) {

      setOccupiedSeats([]);
      setLockedSeats([]);
    }
  };

  useEffect(() => {
    if (!travelDetails) {
      showToast('No se recibieron detalles del viaje. Redirigiendo a rutas de bus…', { type: 'warning' });
      navigate('/busroutes');
      return;
    }

    // Obtener asientos ocupados y bloqueados
    fetchOccupiedSeats();

    // Refrescar cada 30 segundos para ver si hay nuevos bloqueos
    const refreshInterval = setInterval(fetchOccupiedSeats, 30000);

    // Obtener mapeo de números de asiento -> IDs de Seat
    if (travelDetails?.ID_Bus) {
      publicApi.get(`/seats/by_bus/${travelDetails.ID_Bus}`)
        .then(res => {
          const seats = res.data || [];
          const mapping = {};
          seats.forEach(seat => {
            mapping[seat.Number_seat] = seat.ID;
          });
          setSeatMappings(mapping);
        })
        .catch(() => {
          setSeatMappings({});
        });
    }

    return () => clearInterval(refreshInterval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [travelDetails, navigate, tripId, lockSessionId]);

  // Renovar bloqueos cada 10 minutos (antes de que expiren los 15 min)
  useEffect(() => {
    if (selectedSeats.length === 0 || !tripId) return;

    const renewInterval = setInterval(async () => {
      try {
        await publicApi.post('/renew_locks', {
          course_id: tripId,
          session_id: lockSessionId
        });

      } catch (err) {

      }
    }, 10 * 60 * 1000); // 10 minutos

    return () => clearInterval(renewInterval);
  }, [selectedSeats, tripId, lockSessionId]);

  // Limpiar bloqueos al salir de la página
  useEffect(() => {
    const handleBeforeUnload = () => {
      // Usar sendBeacon para enviar incluso al cerrar
      const data = JSON.stringify({ course_id: tripId, session_id: lockSessionId });
      navigator.sendBeacon(
        `${publicApi.defaults.baseURL}/unlock_all_seats`,
        new Blob([data], { type: 'application/json' })
      );
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      // También limpiar al desmontar el componente
      if (tripId && lockSessionId) {
        publicApi.post('/unlock_all_seats', {
          course_id: tripId,
          session_id: lockSessionId
        }).catch(() => {});
      }
    };
  }, [tripId, lockSessionId]);

  // Cargar promociones disponibles para esta ruta
  useEffect(() => {
    const fetchPromotions = async () => {
      if (!travelDetails?.ID_route) return;
      try {
        const res = await publicApi.get(`/promotions_for_route/${travelDetails.ID_route}`);
        const promos = res.data || [];
        setAvailablePromotions(promos);

        // Si hay una promoción automática (sin código), aplicarla por defecto
        const autoPromo = promos.find(p => !p.Code);
        if (autoPromo && !appliedPromotion) {
          setAppliedPromotion(autoPromo);
        }
      } catch (err) {

      }
    };
    fetchPromotions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [travelDetails?.ID_route]);

  // Validar código promocional
  const handleValidatePromoCode = async () => {
    if (!promoCode.trim()) {
      setPromoCodeError('Ingresa un código promocional');
      return;
    }

    setPromoCodeLoading(true);
    setPromoCodeError('');

    try {
      const res = await publicApi.post('/validate_promo_code', {
        code: promoCode.trim(),
        route_id: travelDetails?.ID_route
      });

      if (res.data.valid) {
        setAppliedPromotion(res.data.promotion);
        setPromoCodeError('');
        showToast(`Código aplicado: ${res.data.promotion.name}`, { type: 'success' });
      }
    } catch (err) {

      setPromoCodeError(err.response?.data?.error || 'Código inválido');
      setAppliedPromotion(null);
    } finally {
      setPromoCodeLoading(false);
    }
  };

  // Quitar promoción aplicada
  const handleRemovePromotion = () => {
    setAppliedPromotion(null);
    setPromoCode('');
    setPromoCodeError('');
  };


  // Recalcular seatLayout
  const seatLayout = useMemo(() => {
    return getDetailedBusLayout(busSeatCount);
  }, [busSeatCount]);

  const seatStats = useMemo(() => {
    let totalSeats = 0;
    let available = 0;
    let occupied = occupiedSeats.length;

    const countSeats = (floor) => {
      floor.forEach(row => {
        row.forEach(cell => {
          if (typeof cell === 'object' && cell.type === 'seat') {
            totalSeats++;
            const id = Number(cell.id);
            if (!occupiedSeats.includes(id)) {
              available++;
            }
          }
        });
      });
    };

    countSeats(seatLayout.lower);
    countSeats(seatLayout.upper);

    return {
      total: totalSeats,
      available,
      occupied,
      selected: selectedSeats.length
    };
  }, [seatLayout, occupiedSeats, selectedSeats]);

  // Función para calcular la edad
  const calcularEdad = (fechaNacimiento) => {
    if (!fechaNacimiento) return null;
    const birthDate = new Date(fechaNacimiento);
    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    return age;
  };

  // Constante para descuento de tercera edad (mayores de 60)
  const DESCUENTO_TERCERA_EDAD = 0.50; // 50% de descuento

  // Cálculo del total con descuentos (tercera edad + promociones)
  const { totalAmount, descuentosAplicados, subtotal, descuentoPromocion } = useMemo(() => {
    if (!travelDetails || selectedSeats.length === 0) {
      return { totalAmount: 0, descuentosAplicados: [], subtotal: 0, descuentoPromocion: 0 };
    }

    const unitPrice = Number(travelDetails.Price);
    let total = 0;
    const descuentos = [];

    selectedSeats.forEach(seatId => {
      const info = userDataPerSeat[seatId];
      const edad = calcularEdad(info?.fechaNacimiento);
      let precioAsiento = unitPrice;
      let descuentoAplicado = null;

      // Aplicar descuento de tercera edad (mayores de 60)
      if (edad !== null && edad >= 60) {
        const descuento = unitPrice * DESCUENTO_TERCERA_EDAD;
        precioAsiento = unitPrice - descuento;
        descuentoAplicado = {
          seatId,
          tipo: 'tercera_edad',
          porcentaje: DESCUENTO_TERCERA_EDAD * 100,
          monto: descuento,
          edad
        };
      }

      total += precioAsiento;
      if (descuentoAplicado) {
        descuentos.push(descuentoAplicado);
      }
    });

    // Calcular subtotal después de descuentos por edad
    const subtotalConEdad = total;

    // Aplicar promoción si existe
    let descPromocion = 0;
    if (appliedPromotion) {
      if (appliedPromotion.discount_type === 'percentage') {
        descPromocion = subtotalConEdad * (appliedPromotion.discount / 100);
      } else {
        // Descuento fijo
        descPromocion = Math.min(appliedPromotion.discount, subtotalConEdad);
      }
      total = subtotalConEdad - descPromocion;
    }

    return {
      totalAmount: parseFloat(Math.max(0, total).toFixed(2)),
      descuentosAplicados: descuentos,
      subtotal: parseFloat((unitPrice * selectedSeats.length).toFixed(2)),
      descuentoPromocion: parseFloat(descPromocion.toFixed(2))
    };
  }, [travelDetails, selectedSeats, userDataPerSeat, appliedPromotion]);


  // Handler que alterna selección/deselección de un asiento
  const handleSeatClick = async (seatId) => {
    if (occupiedSeats.includes(seatId) || lockingInProgress) return;

    const seatDbId = seatMappings[seatId];
    if (!seatDbId) {

      return;
    }

    const isCurrentlySelected = selectedSeats.includes(seatId);

    // Verificar sesión solo al seleccionar (no al deseleccionar)
    if (!isCurrentlySelected && !clientId) {
      if (sessionLoading) return; // Aún verificando sesión, esperar
      const ok = await confirm(
        'Para seleccionar asientos y realizar una compra, necesitas iniciar sesión o registrarte.\n\n' +
        '¿Deseas ir a la página de inicio de sesión?',
        { title: 'Inicio de sesión requerido', confirmText: 'Ir a login', cancelText: 'Quedarme aquí', variant: 'info' }
      );
      if (ok) {
        localStorage.setItem('lastTravelDetails', JSON.stringify(travelDetails));
        navigate('/login');
      }
      return;
    }

    if (isCurrentlySelected) {
      // Deseleccionar y desbloquear
      setLockingInProgress(true);
      try {
        await publicApi.post('/unlock_seat', {
          seat_id: seatDbId,
          course_id: tripId,
          session_id: lockSessionId
        });

      } catch (err) {

      }

      setSelectedSeats(prev => prev.filter(id => id !== seatId));
      if (sessionSeatId === seatId) {
        setSessionSeatId(null);
      }
      setUserDataPerSeat(old => {
        const copy = { ...old };
        delete copy[seatId];
        return copy;
      });
      setLockingInProgress(false);
    } else {
      // Seleccionar y bloquear
      setLockingInProgress(true);
      try {
        await publicApi.post('/lock_seat', {
          seat_id: seatDbId,
          course_id: tripId,
          session_id: lockSessionId
        });

        // Bloqueo exitoso, agregar a seleccionados
        setSelectedSeats(prev => [...prev, seatId]);
        showToast(`Asiento ${seatId} seleccionado. Se reservará por 15 minutos.`, { type: 'info' });
        setUserDataPerSeat(old => ({
          ...old,
          [seatId]: {
            nombre: '',
            apellido: '',
            correo: '',
            telefono: '',
            tipoDocumento: 'cedula',
            nacionalidad: 'V',
            identificacion: '',
            fechaNacimiento: '',
            esRepresentado: false,
            cedulaRepresentante: ''
          }
        }));
      } catch (err) {

        if (err.response?.data?.code === 'SEAT_LOCKED') {
          showToast('Este asiento está siendo seleccionado por otro usuario. Por favor elige otro.', { type: 'warning' });
          // Refrescar la lista de asientos ocupados
          fetchOccupiedSeats();
        } else if (err.response?.data?.code === 'SEAT_BOOKED') {
          showToast('Este asiento ya ha sido reservado. Por favor elige otro.', { type: 'warning' });
          fetchOccupiedSeats();
        } else {
          showToast('No se pudo seleccionar el asiento. Intenta de nuevo.', { type: 'error' });
        }
      }
      setLockingInProgress(false);
    }
  };

  // Handler de "Usar mis datos de sesión"
  const handleUseSessionForSeat = async (seatId, checked) => {
    if (occupiedSeats.includes(seatId)) return;

    if (checked) {
      if (sessionSeatId !== null && sessionSeatId !== seatId) {
        showToast('Solo puedes usar tus datos de sesión para un pasajero. Completa manualmente los datos de los demás pasajeros.', { type: 'warning' });
        return;
      }

      setSessionSeatId(seatId);

      const storedUser = localStorage.getItem('currentUser');
      if (!storedUser) {
        showToast('Debes iniciar sesión para usar esta opción.', { type: 'warning' });
        setSessionSeatId(null);
        return;
      }
      let parsed;
      try {
        parsed = JSON.parse(storedUser);
      } catch (error) {
        showToast('Error interno leyendo tu sesión. Inicia sesión de nuevo.', { type: 'error' });
        setSessionSeatId(null);
        return;
      }
      const userId = parsed.user?.id;
      if (!userId) {
        showToast('No se pudo obtener tu usuario. Inicia sesión de nuevo.', { type: 'error' });
        setSessionSeatId(null);
        return;
      }

      try {
        // Obtener datos del usuario y cliente en paralelo
        const [userResp, clientResp] = await Promise.all([
          api.get(`/user/${userId}`),
          api.get('/my_client')
        ]);

        const nombre = userResp.data.Name || '';
        const apellido = userResp.data.Last_name || '';
        const correo = userResp.data.Email || '';
        const telefono = clientResp.data.Number_phone || userResp.data.Number_phone || '';
        const identificacion = clientResp.data.Identification || userResp.data.Identification || '';
        const rawFecha = userResp.data.Birthdate || userResp.data.Birth_date || '';
        const fechaNacimiento = rawFecha ? rawFecha.split('T')[0] : '';

        if (!clientId && clientResp.data.ID) {
          setClientId(clientResp.data.ID);
        }

        setUserDataPerSeat(old => ({
          ...old,
          [seatId]: {
            nombre,
            apellido,
            correo,
            telefono,
            tipoDocumento: 'cedula',
            nacionalidad: 'V',
            identificacion,
            fechaNacimiento,
            esRepresentado: false,
            cedulaRepresentante: ''
          }
        }));
      } catch (error) {
        showToast('No fue posible obtener todos tus datos de sesión. Completa manualmente.', { type: 'warning' });
        setSessionSeatId(null);
        setUserDataPerSeat(old => ({
          ...old,
          [seatId]: {
            nombre: '',
            apellido: '',
            correo: '',
            telefono: '',
            tipoDocumento: 'cedula',
            nacionalidad: 'V',
            identificacion: '',
            fechaNacimiento: '',
            esRepresentado: false,
            cedulaRepresentante: ''
          }
        }));
      }
    } else {
      setSessionSeatId(null);
      setUserDataPerSeat(old => ({
        ...old,
        [seatId]: {
          nombre: '',
          apellido: '',
          correo: '',
          telefono: '',
          tipoDocumento: 'cedula',
          identificacion: '',
          fechaNacimiento: '',
          esRepresentado: false,
          cedulaRepresentante: ''
        }
      }));
    }
  };

  // Handler que actualiza los inputs de un pasajero
  const handlePassengerInputChange = (seatId, e) => {
    const { name, value } = e.target;
    setUserDataPerSeat(old => ({
      ...old,
      [seatId]: {
        ...old[seatId],
        [name]: value
      }
    }));
  };

  // Navegación entre pasos
  const goToStep = (step) => {
    if (step === 2 && selectedSeats.length === 0) {
      showToast('Selecciona al menos un asiento antes de continuar.', { type: 'warning' });
      return;
    }

    if (step === 3) {
      // Validar datos de pasajeros
      for (let seatId of selectedSeats) {
        const info = userDataPerSeat[seatId];

        const nombreTrim = (info?.nombre || '').trim();
        const apellidoTrim = (info?.apellido || '').trim();
        const correoTrim = (info?.correo || '').trim();
        const telefonoTrim = (info?.telefono || '').trim();
        const identificacionTrim = (info?.identificacion || '').trim();
        const fechaNacimientoRaw = info?.fechaNacimiento || '';
        const cedulaRepresentanteTrim = (info?.cedulaRepresentante || '').trim();

        if (sessionSeatId === seatId) {
          if (!nombreTrim || !apellidoTrim || !correoTrim || !telefonoTrim) {
            showToast(`Faltan datos de sesión para el asiento ${seatId}.`, { type: 'warning' });
            return;
          }
          continue;
        }

        if (!nombreTrim || !apellidoTrim || !correoTrim || !telefonoTrim || !identificacionTrim || !fechaNacimientoRaw) {
          showToast(`Completa todos los campos requeridos para el asiento ${seatId} (nombre, apellido, fecha de nacimiento, documento, correo y teléfono).`, { type: 'warning' });
          return;
        }

        // Validar menor de edad
        if (fechaNacimientoRaw) {
          const birthDate = new Date(fechaNacimientoRaw);
          const today = new Date();
          let age = today.getFullYear() - birthDate.getFullYear();
          const monthDiff = today.getMonth() - birthDate.getMonth();
          if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
            age--;
          }

          if (age < 18 && !cedulaRepresentanteTrim) {
            showToast(`El pasajero del asiento ${seatId} es menor de edad. Debe ingresar la cédula del representante.`, { type: 'warning' });
            return;
          }
        }
      }
    }

    setCurrentStep(step);
  };

  // Confirmar Compra
  const handleConfirmPurchase = async () => {
    if (selectedSeats.length === 0) {
      showToast('Selecciona al menos un asiento antes de confirmar la compra.', { type: 'warning' });
      return;
    }

    // Requerir inicio de sesión para completar la compra
    if (!clientId) {
      if (sessionLoading) {
        showToast('Verificando sesión, por favor espera un momento e intenta de nuevo.', { type: 'info' });
        return;
      }
      const ok = await confirm(
        'Para completar tu compra, necesitas iniciar sesión o registrarte.\n\n' +
        '¿Deseas ir a la página de inicio de sesión?\n\n' +
        '(Tus asientos seleccionados se mantendrán reservados por 15 minutos)',
        { title: 'Inicio de sesión requerido', confirmText: 'Ir a login', cancelText: 'Quedarme aquí', variant: 'info' }
      );
      if (ok) {
        localStorage.setItem('pendingPurchase', JSON.stringify({ travelDetails, selectedSeats, userDataPerSeat }));
        navigate('/login');
      }
      return;
    }

    const effectiveClientId = clientId;

    setLoading(true);
    try {







      // Convertir números de asiento a IDs de Seat
      const seatIds = selectedSeats.map(seatNumber => {
        const seatId = seatMappings[seatNumber];
        if (!seatId) {
          throw new Error(`No se encontró el ID para el asiento número ${seatNumber}`);
        }
        return seatId;
      });



      // Preparar datos de pasajeros
      const passengers = selectedSeats.map(seatNumber => {
        const passengerData = userDataPerSeat[seatNumber];
        if (!passengerData) {

          return null;
        }

        return {
          seatNumber: seatNumber,
          seatId: seatMappings[seatNumber],
          nombre: `${(passengerData.nombre || '').trim()} ${(passengerData.apellido || '').trim()}`.trim(),
          correo: passengerData.correo,
          telefono: passengerData.telefono,
          identificacion: passengerData.identificacion,
          fechaNacimiento: passengerData.fechaNacimiento,
          tipoDocumento: passengerData.tipoDocumento || 'cedula',
          nacionalidad: passengerData.nacionalidad || 'V',
          esRepresentado: passengerData.esRepresentado || false,
          cedulaRepresentante: passengerData.cedulaRepresentante || null
        };
      }).filter(p => p !== null);

      const payload = {
        ID_client: effectiveClientId,
        ID_course: tripId,
        seats: seatIds,
        passengers: passengers,
        session_id: lockSessionId
      };




      const resp = await publicApi.post('/booking', payload);
      const newBookingId = resp.data.bookingId;

      // Guardar para la factura
      const invoiceObject = {
        bookingId: newBookingId,
        travelDetails,
        userDataPerSeat,
        selectedSeats,
        totalAmount
      };
      localStorage.setItem('currentInvoice', JSON.stringify(invoiceObject));

      navigate(`/payment/${newBookingId}`);

    } catch (error) {
      showToast('Hubo un problema al procesar la compra. Revisa la consola para más detalles.', { type: 'error' });
    } finally {
      setLoading(false);
    }
  };


  // Si falta travelDetails, mostramos null
  if (!travelDetails) return null;

  // Si busSeatCount es 0, mostramos cargando
  if (busSeatCount === 0) {
    return (
      <div className={styles.container}>
        <h2>Cargando información del bus…</h2>
      </div>
    );
  }

  // Formatear la fecha del viaje
  const tripDateObj = new Date(travelDetails.Departure_date);
  const formattedTripDate = tripDateObj.toLocaleDateString('es-ES', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit'
  });

  // Función para renderizar pisos
  const renderFloor = (floorLayout, floorName) => (
    <div className={styles.floorBlock}>
      <h4 className={styles.floorTitle}>{floorName}</h4>
      <div className={styles.busContainer}>
        <div className={styles.seatLayoutContainer}>
          <div className={styles.busBody}>
            <div className={styles.busFront}>FRENTE</div>
            {floorLayout.map((row, rowIndex) => (
              <div key={`${floorName}-${rowIndex}`} className={styles.seatRow}>
                {row.map((cell, colIndex) => {
                  if (cell === 'aisle') {
                    return <div key={colIndex} className={styles.aisle}></div>;
                  }

                  if (cell.type === 'special' || cell.type === 'empty') {
                    const content = {
                      'driver': 'C',
                      'toilet': 'WC',
                      'stairs1': 'Sub',
                      'stairs2': 'Baj',
                      'food1': '',
                      'food2': '',
                      'empty': '',
                    }[cell.id] || '';

                    return (
                      <div
                        key={colIndex}
                        className={`${styles.cell} ${styles.specialElement} ${styles[cell.id]}`}
                      >
                        {content}
                      </div>
                    );
                  }

                  const seatId = cell.id;
                  const numericSeatId = Number(seatId);
                  const isOccupied = occupiedSeats.includes(numericSeatId);
                  const isLocked = lockedSeats.includes(numericSeatId);
                  const isSelected = selectedSeats.includes(numericSeatId);

                  return (
                    <button
                      key={colIndex}
                      className={`
                        ${styles.cell} ${styles.seat}
                        ${isSelected ? styles.seatSelected : ''}
                        ${isOccupied ? styles.seatOccupied : ''}
                        ${isLocked && !isSelected ? styles.seatLocked : ''}
                      `}
                      onClick={() => handleSeatClick(numericSeatId)}
                      disabled={isOccupied || isLocked || lockingInProgress}
                      title={
                        isOccupied
                          ? `Asiento ${seatId} - Ocupado`
                          : isLocked
                          ? `Asiento ${seatId} - En selección por otro usuario`
                          : `Asiento ${seatId}`
                      }
                    >
                      <span className={styles.seatBack}>
                        <span className={styles.seatNumber}>{seatId}</span>
                      </span>
                    </button>
                  );
                })}
              </div>
            ))}
            <div className={styles.busRear}></div>
          </div>
        </div>
      </div>
    </div>
  );

  // RENDERIZADO
  return (
    <div className={styles.pageContainer}>
      {/* Header con stepper */}
      <div className={styles.header}>
        <h1 className={styles.mainTitle}>Compra de Boletos</h1>
        <div className={styles.stepper}>
          <div className={`${styles.step} ${currentStep >= 1 ? styles.stepActive : ''}`}>
            <div className={styles.stepNumber}>1</div>
            <div className={styles.stepLabel}>Seleccionar Asientos</div>
          </div>
          <div className={styles.stepLine}></div>
          <div className={`${styles.step} ${currentStep >= 2 ? styles.stepActive : ''}`}>
            <div className={styles.stepNumber}>2</div>
            <div className={styles.stepLabel}>Información Pasajeros</div>
          </div>
          <div className={styles.stepLine}></div>
          <div className={`${styles.step} ${currentStep >= 3 ? styles.stepActive : ''}`}>
            <div className={styles.stepNumber}>3</div>
            <div className={styles.stepLabel}>Confirmación y Pago</div>
          </div>
        </div>
      </div>

      <div className={styles.contentWrapper}>
        {/* PASO 1: Selección de Asientos */}
        {currentStep === 1 && (
          <div className={styles.stepContent}>
            <div className={styles.travelInfo}>
              <h3>Detalles del Viaje</h3>
              <div className={styles.infoGrid}>
                <div className={styles.infoItem}>
                  <span className={styles.infoLabel}>Origen:</span>
                  <span className={styles.infoValue}>{travelDetails.Origin}</span>
                </div>
                <div className={styles.infoItem}>
                  <span className={styles.infoLabel}>Destino:</span>
                  <span className={styles.infoValue}>{travelDetails.Destination}</span>
                </div>
                <div className={styles.infoItem}>
                  <span className={styles.infoLabel}>Fecha:</span>
                  <span className={styles.infoValue}>{formattedTripDate}</span>
                </div>
                <div className={styles.infoItem}>
                  <span className={styles.infoLabel}>Hora:</span>
                  <span className={styles.infoValue}>{new Date(travelDetails.Departure_date).toLocaleTimeString('es-ES', {hour: '2-digit', minute: '2-digit'})}</span>
                </div>
                <div className={styles.infoItem}>
                  <span className={styles.infoLabel}>Precio:</span>
                  <span className={styles.infoValue}>${travelDetails.Price}</span>
                </div>
              </div>
            </div>

            <div className={styles.seatSelection}>
              <h3>Selecciona tus Asientos</h3>

              <div className={styles.legend}>
                <div className={styles.legendItem}>
                  <div className={`${styles.legendBox} ${styles.legendAvailable}`}></div>
                  <span>Disponible</span>
                </div>
                <div className={styles.legendItem}>
                  <div className={`${styles.legendBox} ${styles.legendSelected}`}></div>
                  <span>Seleccionado</span>
                </div>
                <div className={styles.legendItem}>
                  <div className={`${styles.legendBox} ${styles.legendOccupied}`}></div>
                  <span>Ocupado/Reservado</span>
                </div>
                {lockedSeats.length > 0 && (
                  <div className={styles.legendItem}>
                    <div className={`${styles.legendBox} ${styles.legendLocked}`}></div>
                    <span>En selección por otro usuario</span>
                  </div>
                )}
              </div>

              {selectedSeats.length > 0 && (
                <div className={styles.alertWarning}>
                  <strong>Tus asientos están reservados temporalmente por 15 minutos.</strong>
                  <br />
                  Completa tu compra antes de que expire el tiempo.
                </div>
              )}

              <div className={styles.seatStats}>
                <div className={styles.statItem}>
                  <span className={styles.statLabel}>Total:</span>
                  <span className={styles.statValue}>{seatStats.total}</span>
                </div>
                <div className={styles.statItem}>
                  <span className={styles.statLabel}>Disponibles:</span>
                  <span className={styles.statValue}>{seatStats.available}</span>
                </div>
                <div className={styles.statItem}>
                  <span className={styles.statLabel}>Ocupados:</span>
                  <span className={styles.statValue}>{seatStats.occupied}</span>
                </div>
                <div className={styles.statItem}>
                  <span className={styles.statLabel}>Seleccionados:</span>
                  <span className={styles.statValue}>{seatStats.selected}</span>
                </div>
              </div>

              {/* Renderizado de pisos */}
              {seatLayout.lower.length > 0 && renderFloor(seatLayout.lower, 'Piso Inferior')}
              {seatLayout.upper.length > 0 && renderFloor(seatLayout.upper, 'Piso Superior')}
            </div>

            <div className={styles.stepActions}>
              <button
                className={styles.btnPrimary}
                onClick={() => goToStep(2)}
                disabled={selectedSeats.length === 0}
              >
                Continuar
              </button>
              <button
                className={styles.btnSecondary}
                onClick={() => navigate('/busroutes')}
              >
                Cancelar
              </button>
            </div>
          </div>
        )}

        {/* PASO 2: Información de Pasajeros */}
        {currentStep === 2 && (
          <div className={styles.stepContent}>
            <div className={styles.passengerForm}>
              <h3>Información de Pasajeros</h3>
              <p className={styles.formDescription}>
                Por favor ingresa los datos de cada pasajero. Los asientos seleccionados son: <strong>{selectedSeats.join(', ')}</strong>
              </p>

              {selectedSeats.map((seatId) => {
                const info = userDataPerSeat[seatId] || {
                  nombre: '',
                  apellido: '',
                  correo: '',
                  telefono: '',
                  tipoDocumento: 'cedula',
                  nacionalidad: 'V',
                  identificacion: '',
                  fechaNacimiento: '',
                  esRepresentado: false,
                  cedulaRepresentante: ''
                };
                const usarSesion = sessionSeatId === seatId;

                return (
                  <div key={seatId} className={styles.passengerCard}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                      <h4 className={styles.passengerTitle}>Asiento {seatId}</h4>
                      <button
                        type="button"
                        onClick={() => handleSeatClick(seatId)}
                        style={{
                          padding: '5px 10px',
                          background: '#dc3545',
                          color: 'white',
                          border: 'none',
                          borderRadius: '5px',
                          cursor: 'pointer',
                          fontSize: '12px'
                        }}
                      >
                        Eliminar Asiento
                      </button>
                    </div>

                    <div className={styles.useSessionCheckbox}>
                      <input
                        type="checkbox"
                        id={`useMyData-${seatId}`}
                        checked={usarSesion || false}
                        onChange={(e) => handleUseSessionForSeat(seatId, e.target.checked)}
                      />
                      <label htmlFor={`useMyData-${seatId}`}>Usar mis datos de sesión</label>
                    </div>

                    <div className={styles.formGrid}>
                      <div className={styles.formField}>
                        <label htmlFor={`nombre-${seatId}`}>Nombre</label>
                        <input
                          id={`nombre-${seatId}`}
                          type="text"
                          name="nombre"
                          value={info.nombre}
                          onChange={(e) => handlePassengerInputChange(seatId, e)}
                          placeholder="Ej: Juan"
                          required
                          disabled={usarSesion}
                        />
                      </div>

                      <div className={styles.formField}>
                        <label htmlFor={`apellido-${seatId}`}>Apellido</label>
                        <input
                          id={`apellido-${seatId}`}
                          type="text"
                          name="apellido"
                          value={info.apellido}
                          onChange={(e) => handlePassengerInputChange(seatId, e)}
                          placeholder="Ej: Pérez"
                          required
                          disabled={usarSesion}
                        />
                      </div>

                      <div className={styles.formField}>
                        <label htmlFor={`fechaNacimiento-${seatId}`}>Fecha de Nacimiento</label>
                        <input
                          id={`fechaNacimiento-${seatId}`}
                          type="date"
                          name="fechaNacimiento"
                          value={info.fechaNacimiento || ''}
                          onChange={(e) => handlePassengerInputChange(seatId, e)}
                          max={new Date().toISOString().split('T')[0]}
                          required
                          disabled={usarSesion}
                          placeholder=""
                        />
                      </div>

                      <div className={styles.formField}>
                        <label htmlFor={`tipoDocumento-${seatId}`}>Tipo de Documento</label>
                        <select
                          id={`tipoDocumento-${seatId}`}
                          name="tipoDocumento"
                          value={info.tipoDocumento || 'cedula'}
                          onChange={(e) => handlePassengerInputChange(seatId, e)}
                          disabled={usarSesion}
                          style={{ padding: '10px', borderRadius: '5px', border: '1px solid #ccc', width: '100%' }}
                        >
                          <option value="cedula">Cédula</option>
                          <option value="pasaporte">Pasaporte</option>
                        </select>
                      </div>

                      {info.tipoDocumento === 'cedula' && (
                        <div className={styles.formField}>
                          <label htmlFor={`nacionalidad-${seatId}`}>Nacionalidad</label>
                          <select
                            id={`nacionalidad-${seatId}`}
                            name="nacionalidad"
                            value={info.nacionalidad || 'V'}
                            onChange={(e) => handlePassengerInputChange(seatId, e)}
                            disabled={usarSesion}
                            style={{ padding: '10px', borderRadius: '5px', border: '1px solid #ccc', width: '100%' }}
                          >
                            <option value="V">V - Venezolano</option>
                            <option value="E">E - Extranjero</option>
                          </select>
                        </div>
                      )}

                      <div className={styles.formField}>
                        <label htmlFor={`identificacion-${seatId}`}>
                          {info.tipoDocumento === 'pasaporte' ? 'Número de Pasaporte' : 'Número de Cédula'}
                        </label>
                        <input
                          id={`identificacion-${seatId}`}
                          type="text"
                          name="identificacion"
                          value={info.identificacion || ''}
                          onChange={(e) => {
                            // Solo permitir números si es cédula
                            if (info.tipoDocumento === 'cedula') {
                              const value = e.target.value.replace(/\D/g, '');
                              handlePassengerInputChange(seatId, { target: { name: 'identificacion', value } });
                            } else {
                              handlePassengerInputChange(seatId, e);
                            }
                          }}
                          placeholder={info.tipoDocumento === 'pasaporte' ? 'Ej: AB1234567' : 'Ej: 12345678'}
                          required
                          disabled={usarSesion}
                        />
                      </div>

                      <div className={styles.formField}>
                        <label htmlFor={`correo-${seatId}`}>Correo Electrónico</label>
                        <input
                          id={`correo-${seatId}`}
                          type="email"
                          name="correo"
                          value={info.correo}
                          onChange={(e) => handlePassengerInputChange(seatId, e)}
                          placeholder="Ej: juan@email.com"
                          required
                          disabled={usarSesion}
                        />
                      </div>

                      <div className={styles.formField}>
                        <label htmlFor={`telefono-${seatId}`}>Teléfono</label>
                        <input
                          id={`telefono-${seatId}`}
                          type="tel"
                          name="telefono"
                          value={info.telefono}
                          onChange={(e) => handlePassengerInputChange(seatId, e)}
                          placeholder="Ej: +58 412 1234567"
                          required
                          disabled={usarSesion}
                        />
                      </div>

                      {/* Campo para menor de edad - cédula del representante */}
                      {info.fechaNacimiento && (() => {
                        const birthDate = new Date(info.fechaNacimiento);
                        const today = new Date();
                        let age = today.getFullYear() - birthDate.getFullYear();
                        const monthDiff = today.getMonth() - birthDate.getMonth();
                        if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
                          age--;
                        }
                        const esMenor = age < 18;

                        if (esMenor) {
                          return (
                            <div className={`${styles.formField} ${styles.minorWarning}`}>
                              <label>
                                Pasajero menor de edad ({age} anos) - Cedula del Representante *
                              </label>
                              <input
                                type="text"
                                name="cedulaRepresentante"
                                value={info.cedulaRepresentante || ''}
                                onChange={(e) => handlePassengerInputChange(seatId, e)}
                                placeholder="Ej: V-12345678 (Cédula del padre, madre o tutor)"
                                required
                              />
                            </div>
                          );
                        }
                        return null;
                      })()}
                    </div>
                  </div>
                );
              })}
            </div>

            <div className={styles.stepActions}>
              <button
                className={styles.btnPrimary}
                onClick={() => goToStep(3)}
              >
                Continuar al Pago
              </button>
              <button
                className={styles.btnSecondary}
                onClick={() => goToStep(1)}
              >
                Volver
              </button>
            </div>
          </div>
        )}

        {/* PASO 3: Confirmación y Pago */}
        {currentStep === 3 && (
          <div className={styles.stepContent}>
            <div className={styles.confirmationSection}>
              <h3>Resumen de tu Compra</h3>

              <div className={styles.summarySection}>
                <h4>Detalles del Viaje</h4>
                <div className={styles.summaryGrid}>
                  <div className={styles.summaryItem}>
                    <span className={styles.summaryLabel}>Ruta:</span>
                    <span className={styles.summaryValue}>
                      <span className={styles.routeOrigin}>{travelDetails.Origin}</span>
                      <span className={styles.routeArrow}>→</span>
                      <span className={styles.routeDestination}>{travelDetails.Destination}</span>
                    </span>
                  </div>
                  <div className={styles.summaryItem}>
                    <span className={styles.summaryLabel}>Fecha:</span>
                    <span className={styles.summaryValue}>{formattedTripDate}</span>
                  </div>
                  <div className={styles.summaryItem}>
                    <span className={styles.summaryLabel}>Hora:</span>
                    <span className={styles.summaryValue}>{new Date(travelDetails.Departure_date).toLocaleTimeString('es-ES', {hour: '2-digit', minute: '2-digit'})}</span>
                  </div>
                </div>
              </div>

              <div className={styles.summarySection}>
                <h4>Asientos Seleccionados</h4>
                <div className={styles.seatList}>
                  {selectedSeats.map(seatId => (
                    <div key={seatId} className={styles.seatBadge}>
                      Asiento {seatId}
                    </div>
                  ))}
                </div>
              </div>

              <div className={styles.summarySection}>
                <h4>Pasajeros</h4>
                {selectedSeats.map(seatId => {
                  const info = userDataPerSeat[seatId];
                  return (
                    <div key={seatId} className={styles.passengerSummary}>
                      <strong>Asiento {seatId}:</strong> {info.nombre} - {info.identificacion}
                    </div>
                  );
                })}
              </div>

              {/* Sección de Código Promocional */}
              <div className={styles.summarySection}>
                <h4>Código Promocional</h4>
                {appliedPromotion ? (
                  <div className={styles.promoApplied}>
                    <div className={styles.promoAppliedContent}>
                      <div className={styles.promoAppliedInfo}>
                        <strong>{appliedPromotion.name}</strong>
                        {appliedPromotion.description && (
                          <p>{appliedPromotion.description}</p>
                        )}
                        <p className={styles.promoDiscount}>
                          Descuento: {appliedPromotion.discount_type === 'percentage' ? `${appliedPromotion.discount}%` : `$${appliedPromotion.discount}`}
                        </p>
                      </div>
                      <button onClick={handleRemovePromotion} className={styles.btnRemovePromo}>
                        Quitar
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className={styles.promoInputContainer}>
                    <div className={styles.promoInputWrapper}>
                      <input
                        type="text"
                        value={promoCode}
                        onChange={(e) => setPromoCode(e.target.value.toUpperCase())}
                        placeholder="Ingresa tu código"
                        className={`${styles.promoInput} ${promoCodeError ? styles.promoInputError : ''}`}
                      />
                      {promoCodeError && (
                        <p className={styles.promoError}>{promoCodeError}</p>
                      )}
                    </div>
                    <button
                      onClick={handleValidatePromoCode}
                      disabled={promoCodeLoading}
                      className={styles.btnApplyPromo}
                    >
                      {promoCodeLoading ? 'Validando...' : 'Aplicar'}
                    </button>
                  </div>
                )}

                {/* Mostrar promociones automáticas disponibles */}
                {!appliedPromotion && availablePromotions.filter(p => !p.Code).length > 0 && (
                  <div className={styles.promoAvailable}>
                    <p>Promociones disponibles para esta ruta:</p>
                    {availablePromotions.filter(p => !p.Code).map(promo => (
                      <div key={promo.ID} className={styles.promoAvailableItem}>
                        <span>
                          {promo.Name} - {promo.Discount_type === 'percentage' ? `${promo.Discount}%` : `$${promo.Discount}`}
                        </span>
                        <button
                          onClick={() => setAppliedPromotion({
                            id: promo.ID,
                            name: promo.Name,
                            description: promo.Description,
                            discount: promo.Discount,
                            discount_type: promo.Discount_type
                          })}
                          className={styles.btnApplyAvailable}
                        >
                          Aplicar
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className={styles.totalSection}>
                <div className={styles.totalRow}>
                  <span className={styles.totalLabel}>Precio por asiento:</span>
                  <span className={styles.totalValue}>${travelDetails.Price}</span>
                </div>
                <div className={styles.totalRow}>
                  <span className={styles.totalLabel}>Cantidad de asientos:</span>
                  <span className={styles.totalValue}>{selectedSeats.length}</span>
                </div>
                <div className={styles.totalRow}>
                  <span className={styles.totalLabel}>Subtotal:</span>
                  <span className={styles.totalValue}>${subtotal}</span>
                </div>

                {/* Mostrar descuentos de tercera edad */}
                {descuentosAplicados.length > 0 && (
                  <div className={styles.discountBadge}>
                    <strong>Descuentos por Tercera Edad:</strong>
                    {descuentosAplicados.map((desc, idx) => (
                      <div key={idx}>
                        Asiento {desc.seatId}: ({desc.edad} anos) - {desc.porcentaje}% = -${desc.monto.toFixed(2)}
                      </div>
                    ))}
                  </div>
                )}

                {/* Mostrar descuento de promoción */}
                {appliedPromotion && descuentoPromocion > 0 && (
                  <div className={styles.alertInfo}>
                    <strong>Promoción Aplicada:</strong>
                    <div style={{ marginTop: '5px' }}>
                      {appliedPromotion.name} ({appliedPromotion.discount_type === 'percentage' ? `${appliedPromotion.discount}%` : `$${appliedPromotion.discount}`}) = -${descuentoPromocion.toFixed(2)}
                    </div>
                  </div>
                )}

                <div className={styles.totalRowFinal}>
                  <span className={styles.totalLabel}>Total a Pagar:</span>
                  <span className={styles.totalValueFinal}>${totalAmount}</span>
                </div>
              </div>
            </div>

            <div className={styles.stepActions}>
              <button
                className={styles.btnPrimary}
                onClick={handleConfirmPurchase}
                disabled={loading}
              >
                {loading ? 'Procesando...' : 'Confirmar y Pagar'}
              </button>
              <button
                className={styles.btnSecondary}
                onClick={() => goToStep(2)}
                disabled={loading}
              >
                Volver
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PurchaseConfirmation;
